import React, { createContext, ReactNode, useMemo } from "react"

import { getAddOnPricing } from "./cart/getAddOnPricing"
import { getAddonsInfoOfCartItem } from "./cart/getAddonsInfoOfCartItem"
import { getBookingFieldsInfoOfCartItem } from "./cart/getBookingFieldsInfoOfCartItem"
import { getGroupedAddonsOfAOrderItem } from "./order/getGroupedAddonsOfAOrderItem"
import { getParticipantFieldsInfoOfOrderItem } from "./order/getParticipantFieldsInfoOfOrderItem"
import { useHandleDeleteOrderItem } from "./order/handleDeleteOrderItem"
import { useHandleRebookWithAnotherProduct } from "./order/handleRebookWithAnotherProduct"
import { useHandleRescheduleActivityBooking } from "./order/handleRescheduleActivityBooking"
import { useHandleRescheduleActivityBookingWithFields } from "./order/handleRescheduleActivityBookingWithFields"
import { useHandleUpdateBookingAddonDetails } from "./order/handleUpdateBookingAddonDetails"
import { useHandleUpdateBookingDetailsOfOrderItem } from "./order/handleUpdateBookingDetailsOfOrderItem"
import { useHandleUpdateCustomerInOrder } from "./order/handleUpdateCustomerInOrder"
import { useHandleUpdateDynamicRentalBookingDetails } from "./order/handleUpdateDynamicRentalBookingDetails"
import { useHandleUpdateMerchandiseQuantityOfOrderItem } from "./order/handleUpdateMerchandiseQuantityOfOrderItem"
import { useHandleUpdateParticipantDetailsOfOrderItem } from "./order/handleUpdateParticipantDetailsOfOrderItem"
import { useHandleUpdatePriceOfOrderItem } from "./order/handleUpdatePriceOfOrderItem"

export interface OrderContextProps {
  getGroupedAddonsOfAOrderItem?: typeof getGroupedAddonsOfAOrderItem
  handleUpdateMerchandiseQuantityOfOrderItem?: ReturnType<
    typeof useHandleUpdateMerchandiseQuantityOfOrderItem
  >["handleUpdateMerchandiseQuantityOfOrderItem"]
  getBookingFieldsInfoOfCartItem?: typeof getBookingFieldsInfoOfCartItem
  getAddonsInfoOfCartItem?: typeof getAddonsInfoOfCartItem
  getAddOnPricing?: typeof getAddOnPricing
  handleUpdatePriceOfOrderItem?: ReturnType<
    typeof useHandleUpdatePriceOfOrderItem
  >["handleUpdatePriceOfOrderItem"]
  handleDeleteOrderItem?: ReturnType<typeof useHandleDeleteOrderItem>["handleDeleteOrderItem"]
  handleRescheduleActivityBooking?: ReturnType<
    typeof useHandleRescheduleActivityBooking
  >["handleRescheduleActivityBooking"]
  handleRescheduleActivityBookingWithFields?: ReturnType<
    typeof useHandleRescheduleActivityBookingWithFields
  >["handleRescheduleActivityBookingWithFields"]
  handleUpdateBookingDetailsOfOrderItem?: ReturnType<
    typeof useHandleUpdateBookingDetailsOfOrderItem
  >["handleUpdateBookingDetailsOfOrderItem"]
  handleUpdateBookingAddonDetails?: ReturnType<
    typeof useHandleUpdateBookingAddonDetails
  >["handleUpdateBookingAddonDetails"]
  handleUpdateDynamicRentalBookingDetails?: ReturnType<
    typeof useHandleUpdateDynamicRentalBookingDetails
  >["handleUpdateDynamicRentalBookingDetails"]
  getParticipantFieldsInfoOfOrderItem?: typeof getParticipantFieldsInfoOfOrderItem
  handleUpdateParticipantDetailsOfOrderItem?: ReturnType<
    typeof useHandleUpdateParticipantDetailsOfOrderItem
  >["handleUpdateParticipantDetailsOfOrderItem"]
  handleUpdateCustomerInOrder?: ReturnType<
    typeof useHandleUpdateCustomerInOrder
  >["handleUpdateCustomerInOrder"]
  handleRebookWithAnotherProduct?: ReturnType<
    typeof useHandleRebookWithAnotherProduct
  >["handleRebookWithAnotherProduct"]
}

const defaultValues: OrderContextProps = {}

export const OrderContext = createContext<OrderContextProps>(defaultValues)

interface OrderProviderProps {
  children: ReactNode
}

const OrderProvider = ({ children }: OrderProviderProps) => {
  const { handleUpdateMerchandiseQuantityOfOrderItem } =
    useHandleUpdateMerchandiseQuantityOfOrderItem()
  const { handleUpdatePriceOfOrderItem } = useHandleUpdatePriceOfOrderItem()
  const { handleDeleteOrderItem } = useHandleDeleteOrderItem()
  const { handleRescheduleActivityBooking } = useHandleRescheduleActivityBooking()
  const { handleRescheduleActivityBookingWithFields } =
    useHandleRescheduleActivityBookingWithFields()
  const { handleUpdateBookingDetailsOfOrderItem } = useHandleUpdateBookingDetailsOfOrderItem()
  const { handleUpdateBookingAddonDetails } = useHandleUpdateBookingAddonDetails()
  const { handleUpdateDynamicRentalBookingDetails } = useHandleUpdateDynamicRentalBookingDetails()
  const { handleUpdateParticipantDetailsOfOrderItem } =
    useHandleUpdateParticipantDetailsOfOrderItem()
  const { handleUpdateCustomerInOrder } = useHandleUpdateCustomerInOrder()
  const { handleRebookWithAnotherProduct } = useHandleRebookWithAnotherProduct()

  const orderContextValue: OrderContextProps = useMemo(
    () => ({
      getGroupedAddonsOfAOrderItem,
      getParticipantFieldsInfoOfOrderItem,
      handleUpdateMerchandiseQuantityOfOrderItem,
      getBookingFieldsInfoOfCartItem,
      getAddonsInfoOfCartItem,
      getAddOnPricing,
      handleUpdatePriceOfOrderItem,
      handleDeleteOrderItem,
      handleRescheduleActivityBooking,
      handleRescheduleActivityBookingWithFields,
      handleUpdateBookingDetailsOfOrderItem,
      handleUpdateBookingAddonDetails,
      handleUpdateDynamicRentalBookingDetails,
      handleUpdateParticipantDetailsOfOrderItem,
      handleUpdateCustomerInOrder,
      handleRebookWithAnotherProduct,
    }),
    [
      handleUpdateMerchandiseQuantityOfOrderItem,
      handleUpdatePriceOfOrderItem,
      handleDeleteOrderItem,
      handleRescheduleActivityBooking,
      handleRescheduleActivityBookingWithFields,
      handleUpdateBookingDetailsOfOrderItem,
      handleUpdateBookingAddonDetails,
      handleUpdateDynamicRentalBookingDetails,
      handleUpdateParticipantDetailsOfOrderItem,
      handleUpdateCustomerInOrder,
      handleRebookWithAnotherProduct,
    ]
  )

  return <OrderContext.Provider value={orderContextValue}>{children}</OrderContext.Provider>
}

export default OrderProvider
